/**
 * 우클릭한 DSD 셀이 어떤 dimension 태그로 매핑되는지 보여주는 셀 앵커 popover.
 *
 * - 헤더 셀: 그 셀의 태그 1개.
 * - 데이터 셀: 지배 헤더들의 태그를 합친 여러 개(계정과목/기간/축 멤버).
 *
 * 클릭된 셀(`data-cell-id`)에 Radix Popover 를 anchor 시키고, 카드는
 * pointer-events-none 로 아래 셀 클릭을 가리지 않는다.
 */

import { useMemo } from "react";
import * as Popover from "@radix-ui/react-popover";
import { PeriodType } from "@/apis/compare/route";

const PERIOD_LABELS: Record<PeriodType, string> = {
  CURRENT: "당기",
  PREVIOUS: "전기",
  BEFORE_PREVIOUS: "전전기",
};

/** 헤더 태그·표 공유 태그가 공통으로 갖는 dimension 필드. */
export interface TagDimension {
  lineItem?: string | null;
  period?: PeriodType | null;
  member?: string | null;
  label?: string | null;
}

interface TagRow {
  /** dimension 종류 라벨 */
  label: string;
  /** 매핑된 값(한글 라벨 우선, 없으면 raw id) */
  value: string;
}

/**
 * 한 태그가 가진 dimension 들을 (라벨, 값) 행으로 모두 변환.
 * 백엔드 변경으로 하나의 헤더 태그가 기간·계정과목을 동시에 가질 수 있어
 * 채워진 dimension 마다 행이 나온다(없으면 빈 배열).
 */
function tagToRows(tag: TagDimension): TagRow[] {
  const rows: TagRow[] = [];
  if (tag.period)
    rows.push({
      label: "기간",
      value: PERIOD_LABELS[tag.period] ?? tag.period,
    });
  if (tag.lineItem)
    rows.push({ label: "계정과목", value: tag.label || tag.lineItem });
  if (tag.member)
    rows.push({ label: "축 멤버", value: tag.label || tag.member });
  return rows;
}

// dimension 종류 표시 순서: 기간 → 계정과목(행) → 축 멤버.
const ROW_ORDER = ["기간", "계정과목", "축 멤버"] as const;

/**
 * 여러 태그를 dimension 종류별로 묶어 한 종류당 한 행으로 만든다.
 * 같은 종류에 값이 여럿이면(축 멤버) 중복 제거 후 쉼표로 이어 보여준다.
 */
function toRows(tags: TagDimension[]): TagRow[] {
  const valuesByLabel = new Map<string, string[]>();
  for (const tag of tags) {
    for (const row of tagToRows(tag)) {
      const list = valuesByLabel.get(row.label) ?? [];
      if (!list.includes(row.value)) list.push(row.value);
      valuesByLabel.set(row.label, list);
    }
  }
  return ROW_ORDER.filter((label) => valuesByLabel.has(label)).map((label) => ({
    label,
    value: valuesByLabel.get(label)!.join(", "),
  }));
}

/** 클릭한 셀이 백엔드가 헤더로 인식한 셀인지, 데이터 셀인지. */
export type ReviewCellKind = "header" | "data";

/** 셀 종류별 popover 헤더 문구 — 헤더/데이터 셀을 구분해 보여준다. */
const KIND_TITLE: Record<ReviewCellKind, string> = {
  header: "헤더 셀 · 인식된 태그",
  data: "데이터 셀 · 매핑된 태그",
};

interface HeaderTagPopoverProps {
  /** DSD 프리뷰 컨테이너 — 앵커 셀 탐색 범위 */
  containerRef: React.RefObject<HTMLElement | null>;
  /** 열린 셀 dsdId (null 이면 닫힘) */
  dsdId: string | null;
  /** 클릭한 셀 종류 — 헤더 셀이면 인식된 태그, 데이터 셀이면 매핑된 태그를 표시 */
  kind: ReviewCellKind | null;
  /** 표시할 태그들(헤더 셀: 인식된 태그 / 데이터 셀: 지배 헤더 + 표 공유 태그) */
  tags: TagDimension[] | null;
  onClose: () => void;
}

export const HeaderTagPopover = ({
  containerRef,
  dsdId,
  kind,
  tags,
  onClose,
}: HeaderTagPopoverProps) => {
  // dsdId 가 바뀔 때마다 앵커 셀을 다시 찾아 Radix 가 재측정하게 한다.
  const anchorEl = useMemo<HTMLElement | null>(() => {
    if (!dsdId) return null;
    return (
      containerRef.current?.querySelector<HTMLElement>(
        `[data-cell-id="${CSS.escape(dsdId)}"]`,
      ) ?? null
    );
  }, [containerRef, dsdId]);

  const anchorRef = useMemo<{ current: HTMLElement | null }>(
    () => ({ current: anchorEl }),
    [anchorEl],
  );

  const rows = useMemo(() => (tags ? toRows(tags) : []), [tags]);

  if (!dsdId || !anchorEl || rows.length === 0) return null;

  return (
    <Popover.Root open onOpenChange={(open) => !open && onClose()}>
      <Popover.Anchor virtualRef={anchorRef} />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={16}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          // pointer-events-none: 정보 표시 전용 카드라 아래 셀 클릭을 가리지 않고
          // 그대로 통과시킨다. 다른 셀을 클릭하면 outside dismiss + dispatcher 가
          // 태그를 다시 계산해 popover 가 자연스럽게 갱신/닫힌다.
          className="pointer-events-none z-50 flex flex-col gap-1 rounded-[8px] border border-line-normal bg-background-base px-3 py-2 shadow-[0_4px_6px_-1px_#0000001A,0_2px_4px_-2px_#0000001A]"
        >
          <span className="caption-2-medium text-label-alternative">
            {KIND_TITLE[kind ?? "data"]}
          </span>
          {rows.map((row) => (
            <div
              key={`${row.label} ${row.value}`}
              className="flex items-center gap-1.5"
            >
              <span className="caption-2-medium text-label-alternative shrink-0">
                {row.label}
              </span>
              <span className="label-2-medium text-label-normal break-all">
                {row.value}
              </span>
            </div>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
